import {
  IUserResponse,
  TExercise,
  TGetUserCurrentFlowRequest,
  TGetUserCurrentFlowResponse,
  TInsertAllAnswersResponse,
  TQuestionairResponse,
  TContactPermissions,
} from "../types/auth.types";
import { TUploadResponse } from "../types/requests.types";
import {
  IAppointmentProgram,
  IFaq,
  ITreatmentPlan,
  TAppointmentResponse,
  TCalendarAppointment,
  TCancelAppointmentReason,
  TCancelAppointmentRequest,
  TGetQuestionByIdResponse,
  THelpAppointment,
  TInsertAnswerRequest,
  TUpdateUserRequest,
  TUpdateUserSettingsRequest,
  IProtocolComponentsResponse,
  TCreateAppointmentRequest,
  IQuestionnaireTasksResponse,
  IMyHotlineQuestionnaireResponse,
} from "../types/user.types";
import { 
  IValidateAppointmentChangeRequest, 
  IValidateAppointmentChangeResponse,
  ITherapistMonthlyAvailabilityResponse,
  IDeviceBookingAvailabilityResponse,
  IVerifyOtcByGuidResponse
} from "../../pages/calendar/types/appointments.types";
import { api } from "./api";
import { IResponse } from "../types";
import {
  TMakeAppointmentRequest,
  TUpdateAppointmentRequest,
  TInsertAllAnswersRequest,
} from "../types/user.types";
import { IAdminMessage, IAppointment, IUpdate, IUploadedFiles, IUser } from "../../types/types";
import { getCookie } from "../../utils/token";

export const userApi = api.injectEndpoints({
  endpoints: (build) => ({
    getUser: build.query<IUserResponse, void>({
      query: () => ({
        url: `/user`,
        method: "GET",
      }),
    }),
    getContactDetails: build.query<IUserResponse, { patientId?: number | string } | void>({
      query: (params) => ({
        url: params?.patientId ? `/user/contact-details/${params.patientId}` : `/user/contact-details`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${getCookie("activeContactToken") ?? ""}`,
        },
      }),
    }),
    getUsers: build.query<IUserResponse, string>({
      query: () => ({
        url: `user/testusers`,
        method: "GET",
      }),
    }),
    getCurrentFlow: build.query<
      TGetUserCurrentFlowResponse,
      TGetUserCurrentFlowRequest
    >({
      query: ({ usersFlowId, userId }) => ({
        url: `/Questions/GetUserCurrentFlow/${usersFlowId}/${userId}`,
        method: "GET",
      }),
    }),
    updateUserDetails: build.mutation<IUserResponse, TUpdateUserRequest>({
      query: ({ body, guid }) => ({
        url: `user/${guid}`,
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    }),
    updateUserSettings: build.mutation<
      IUserResponse,
      TUpdateUserSettingsRequest
    >({
      query: ({ body, id }) => ({
        url: `user/settings/${id}`,
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    }),

    makeAppointment: build.mutation<IResponse, TMakeAppointmentRequest>({
      query: ({ date, therapistId }) => ({
        url: `/users/appointments`,
        method: "POST",
        body: { therapistId, date },
      }),
    }),
    updateAppointment: build.mutation<IResponse, TUpdateAppointmentRequest>({
      query: (body) => ({
        url: `/users/appointments`,
        method: "Patch",
        body,
      }),
    }),
    cancelAppointmentLocal: build.mutation<
      IResponse,
      TCancelAppointmentRequest
    >({
      query: (body) => ({
        url: `/users/appointments`,
        method: "DELETE",
        body,
      }),
    }),
    fetchDocs: build.query<IUploadedFiles[], string>({
      query: (UsersGuid) => ({
        url: `/files/FileUploads/2/${UsersGuid}`,
        method: "POST",
      }),
    }),
    sendFile: build.mutation<IResponse, { UsersGuid: string, formData: FormData }>({
      query: ({ UsersGuid, formData }) => {
        return {
          url: `/files/FileUploads/1/${UsersGuid}`,
          method: "POST",
          body: formData,
        };
      },
    }),
    fetchPrescriptions: build.query<IResponse, void>({
      query: () => ({
        url: `/users/prescriptions`,
        method: "GET",
      }),
    }),
    updatePrescription: build.mutation<IResponse, string>({
      query: (prescriptionId) => ({
        url: `/users/prescriptions`,
        method: "POST",
        body: { prescriptionId },
      }),
    }),
    getQuestionByQuestionnaireId: build.query<
      TGetQuestionByIdResponse,
      { currentQuestionnAirId: number; nextQuestionId: number; skip: boolean }
    >({
      query: ({ currentQuestionnAirId, nextQuestionId }) => ({
        url: `Questions/GetQuestionByQuestionnaireId/${currentQuestionnAirId}/${nextQuestionId}/0`,
        // url: `Questions/GetQuestionByQuestionnaireId/26/11/0`,
        method: "GET",
      }),
    }),
    getPOCQuestionById: build.query<
      TGetQuestionByIdResponse,
      { nextQuestionId: number; skip: boolean }
    >({
      query: ({ nextQuestionId }) => ({
        url: `Questions/GetQuestionByQuestionnaireId/26/${nextQuestionId}/0`,
        method: "GET",
      }),
    }),
    getNextQuestionByQuestionnaireId: build.query<
      TGetQuestionByIdResponse,
      { currentQuestionId: number; nextQuestionId: number; skip: boolean }
    >({
      query: ({ currentQuestionId, nextQuestionId }) => ({
        url: `Questions/GetQuestionByQuestionnaireId/${currentQuestionId}/${nextQuestionId}/0`,
        method: "GET",
      }),
    }),
    insertAnswerQuestion: build.mutation<IResponse, TInsertAnswerRequest>({
      query: (option) => ({
        url: `/Questions/InsertAnswerQuestion`,
        method: "POST",
        body: JSON.stringify(option),
      }),
    }),
    getMessageByUserId: build.query<IUpdate[], void>({
      query: () => ({
        url: `/myday/GetMessagesByUserId`,
        method: "GET",
      }),
    }),
    getAdminMessages: build.query<IAdminMessage[], { companyId: number }>({
      query: ({ companyId }) => ({
        url: `Admintech/Admin_Messages_DML/1/${companyId}/1`,
        method: "POST",
        body: {},
      }),
    }),
    GetCurrentQuestionnaireByUsersId: build.query<
      TQuestionairResponse,
      { guid: string; userId: number }
    >({
      query: ({ guid }) => ({
        url: `/Questions/GetCurrentQuestionnaireByUsersId/${guid}`,
        method: "GET",
      }),
    }),
    /**
     * The questionnaires this patient's treatment protocols have asked for. Completed ones come
     * back too, so the tab can show what has already been done rather than making items vanish.
     */
    /**
     * The hotline questionnaire the patient owes, if any.
     *
     * Its own endpoint rather than folded into QuestionnaireTasks/Mine: the two rules live in
     * different features and different branches, and the tab merges them at render time instead.
     */
    getMyHotlineQuestionnaire: build.query<IMyHotlineQuestionnaireResponse, void>({
      query: () => ({
        url: "/MyHotlineQuestionnaire",
        method: "GET",
      }),
      providesTags: ["HotlineQuestionnaire"],
    }),

    getMyQuestionnaireTasks: build.query<
      IQuestionnaireTasksResponse,
      { openOnly?: boolean } | void
    >({
      query: (args) => ({
        url: `/QuestionnaireTasks/Mine?openOnly=${args && args.openOnly ? "true" : "false"}`,
        method: "GET",
      }),
      providesTags: ["QuestionnaireTasks"],
    }),

    getAppointmentsByUserId: build.query<
      IAppointment[],
      void
    >({
      query: () => ({
        url: `/myday/GetAppointmentsByUserId`,
        method: "GET",
      }),
      providesTags: ['UserAppointments'],
      keepUnusedDataFor: 1,
    }),
    /**
     * Whether this patient may book their own appointments.
     *
     * A device treatment is scheduled for them and not by them: it needs a technician, a free
     * machine, and the montage their protocol prescribes. The server refuses such a booking
     * regardless — this is what lets the app stop offering it in the first place.
     */
    getSelfBookingPolicy: build.query<
      { canSelfBook: boolean; reason: string | null },
      void
    >({
      query: () => ({
        url: `/myday/GetSelfBookingPolicy`,
        method: "GET",
      }),
      transformResponse: (response: { canSelfBook?: boolean; reason?: string | null }) => ({
        // A missing answer must not read as "blocked" — the server is the one that decides.
        canSelfBook: response?.canSelfBook !== false,
        reason: response?.reason ?? null,
      }),
      providesTags: ['UserAppointments'],
    }),
    getExercisesByUserGuid: build.mutation<
      TExercise[],
      void
    >({
      query: () => ({
        url: `/user/UserExercises_Select`,
        method: "GET",
      }),
    }),
    insertAllAnswersQuestions: build.mutation<
      TInsertAllAnswersResponse,
      {
        answers: TInsertAllAnswersRequest[];
        questionnaireCompanyId: number;
        targetUserGuid: string;
      }
    >({
      query: ({ answers, questionnaireCompanyId, targetUserGuid }) => {
        return {
          url: `/Questions/InsertAllAnswersQuestions/${questionnaireCompanyId}/0/0?targetUserGuid=${targetUserGuid ?? ""}`,
          method: "POST",
          body: JSON.stringify(answers),
        };
      },
    }),
    insertAudioAnswersQuestions: build.mutation<
      TInsertAllAnswersResponse,
      {
        answers: { questionId: number; file: Blob }[];
        companyId: number;
        questionnaireCompaniesScoreUsersId: number;
        guid: string | null;
      }
    >({
      query: ({
        answers,
        companyId,
        questionnaireCompaniesScoreUsersId,
        guid,
      }) => {

        const formData = new FormData();

        // הוספת questionnaireCompaniesScoreUsersId פעם אחת בלבד
        formData.append(
          "questionnaireCompaniesScoreUsersId",
          questionnaireCompaniesScoreUsersId.toString()
        );

        // הוספת כל קבצי האודיו
        answers.forEach((answer, index) => {
          const { file } = answer;
          const fileName = `${questionnaireCompaniesScoreUsersId}_${answer.questionId}.opus`;
          const fileWithProperName = new File([file], fileName, {
            type: 'audio/opus',
          });
          formData.append("files", fileWithProperName);
        });

        return {
          url: `files/InsertAllAudioAnswersQuestions/${companyId}/${guid}`,
          method: "POST",
          body: formData,
        };
      },
    }),
    postFiles: build.mutation<
      TUploadResponse,
      {
        files: { file: File; questionId: number }[];
        companyId?: number;
        questionnaireCompaniesScoreUsersId: number;
        userId: string | null;
        type?: number;
      }
    >({
      query: ({
        files,
        companyId,
        questionnaireCompaniesScoreUsersId,
        userId,
        type = 2,
      }) => {
        const formData = new FormData();

        // Append multiple files to FormData
        files.forEach(({ file, questionId }) => {
          const extension = file.name.split(".").pop();
          const fileName = `${questionnaireCompaniesScoreUsersId}_${questionId}.${extension}`;
          const renamedFile = new File([file], fileName, {
            type: file.type,
          });
          formData.append("files", renamedFile);
          formData.append(
            "questionnaireCompaniesScoreUsersId",
            questionnaireCompaniesScoreUsersId.toString()
          );
        });

        return {
          url: `/files/UploadFiles/${type}/${userId}`,
          method: "POST",
          body: formData,
        };
      },
    }),
    getQuestionnaireByGuidAndType: build.query<
      TQuestionairResponse,
      { guid: string; typeId?: number; isForTherapists?: boolean }
    >({
      query: ({ typeId = 2, guid, isForTherapists = false }) => ({
        url: `/Questions/GetCurrentQuestionnaireByType/${guid}/${typeId}${isForTherapists ? `/${isForTherapists}` : ''}`,
        method: "GET",
        keepUnusedDataFor: 1,
      }),
    }),

    getAllRelevantTherapistForUser: build.query<
      IUser[],
      { Type: number; UsersGuid: string }
    >({
      query: ({ Type, UsersGuid }) => ({
        url: `/user/GetTherapistsByClientUsersId/${Type}/${UsersGuid}`,
        method: "GET",
      }),
      providesTags: (result) =>
        result
          ? [
            ...result.map(({ id }) => ({ type: 'Therapist' as const, id })),
            { type: 'Therapist', id: 'LIST' },
          ]
          : [{ type: 'Therapist', id: 'LIST' }],
    }),
    postAppointments: build.mutation<
      TAppointmentResponse,
      {
        type?: number;
        appointment: THelpAppointment;
        QuestionnaireCompaniesScoreUsersId: number | null;
      }
    >({
      query: ({ type = 2, appointment, QuestionnaireCompaniesScoreUsersId }) => ({
        url: `/Appointments/Appointments_PostAppointments/${type}/${QuestionnaireCompaniesScoreUsersId ?? ""
          }`,
        method: "POST",
        body: JSON.stringify(appointment),
      }),
    }),
    getAllCancelAppointmentsReasons: build.mutation<
      TCancelAppointmentReason[],
      { appointment: { Id: number; AnotherReason: string } }
    >({
      query: ({ appointment }) => ({
        url: `/Appointments/Appointments_CancelApp/1`,
        method: "POST",
        body: appointment,
      }),
    }),
    cancelAppointment: build.mutation<
      TAppointmentResponse,
      { appointment: TCancelAppointmentRequest }
    >({
      query: ({ appointment }) => ({
        url: `/Appointments/Appointments_CancelApp/2`,
        method: "POST",
        body: JSON.stringify(appointment),
      }),
    }),
    getAllOpenTreatmentPlansForUser: build.mutation<
      ITreatmentPlan[],
      { companyId: number; usersName: string; UsersGuid: string }
    >({
      query: ({ companyId, usersName, UsersGuid }) => ({
        url: `/Appointments/AppointmentsPrograms_ForClient/1`,
        method: "POST",
        body: {
          Name: usersName,
          UsersGuid: UsersGuid,
          companyId: companyId,
        },
      }),
    }),
    getAllAppointmentsInProgram: build.mutation<
      TCalendarAppointment[],
      IAppointmentProgram
    >({
      query: (treatmentPlan) => ({
        url: `/Appointments/AppointmentsPrograms_ForClient/10`,
        method: "POST",
        body: treatmentPlan,
      }),
    }),
    alternativeAppointmentProgramDate: build.mutation<
      TCalendarAppointment[],
      IAppointmentProgram
    >({
      query: (treatmentPlan) => ({
        url: `/Appointments/AppointmentsPrograms_ForClient/9`,
        method: "POST",
        body: treatmentPlan,
      }),
    }),
    validatePassword: build.mutation<boolean, string>({
      query: (password) => ({
        url: `/user/ValidateRightPassword`,
        method: "POST",
        body: JSON.stringify(password),
      }),
    }),
    uploadFile: build.mutation<
      IResponse,
      { form: FormData; usersId: number | string; type: number }
    >({
      query: ({ form, type, usersId }) => ({
        url: `/files/UploadFiles/${type}/${usersId}`,
        method: "POST",
        body: form,
      }),
    }),
    deleteUserAvatar: build.mutation<IResponse, { guid: string }>({
      query: ({ guid }) => ({
        url: `user/${guid}/avatar`,
        method: "DELETE",
      }),
    }),
    getFaqs: build.query<IFaq[], number>({
      query: (companyId) => ({
        url: `Admintech/Admin_Faq_DML/0/${companyId}`,
        method: "POST",
        body: [],
      }),
    }),

    // Consent endpoints
    getGeneralConfirmReadStatus: build.query<
      { hasConsent: boolean },
      { clientUsersGuid: string }
    >({
      query: ({ clientUsersGuid }) => ({
        url: `General/General_ConfirmRead/1/2/${clientUsersGuid}`,
        method: "GET",
      }),
      providesTags: ["Consent"],
    }),

    generalConfirmRead: build.mutation<
      { success: boolean },
      { clientUsersGuid: string }
    >({
      query: ({ clientUsersGuid }) => ({
        url: `General/General_ConfirmRead/2/2/${clientUsersGuid}`,
        method: "GET",
      }),
      invalidatesTags: ["Consent"],
    }),
    getAnalysis: build.query<
      {
        documentAnalysis: any;
        videoAnalysis: string;
        voiceAnalysis: string;
        textAnalysis: any;
      },
      { type: number; questId: number }
    >({
      query: ({ type, questId }) => ({
        url: `AI/getAnalysis/${type}/${questId}`,
        method: "POST",
      }),
    }),

    // Protocol components endpoint
    getProtocolComponents: build.query<
      IProtocolComponentsResponse,
      number
    >({
      query: (protocolId) => ({
        url: `/api/protocols/${protocolId}/components`,
        method: "GET",
      }),
    }),
    switchPatient: build.mutation<
      { success: boolean; accessToken: string; user: IUser; contactPermissions?: TContactPermissions | null },
      { patientId: number | string }
    >({
      query: ({ patientId }) => ({
        url: `/user/switch-patient`,
        method: "POST",
        
        body: { PatientId: patientId },
        headers: { 
          Authorization:  `Bearer ${getCookie("activeContactToken") ?? ""}`,
        },
      }),
    }),
    createAppointmentFast: build.mutation<
      TAppointmentResponse,
      {
        appointment: Partial<TCreateAppointmentRequest & { id?: number; appointmentsTypeId?: number; statusId?: number }>;
        repeatCount?: number;
      }
    >({
      query: ({ appointment, repeatCount = 1 }) => ({
        url: `Appointments/CreateAppointmentFast`,
        method: "POST",
        body: { 
          ...appointment, 
          id: 0, 
          appointmentsTypeId: 1, 
          statusId: 1 
        },
        params: { repeatCount },
      }),
      invalidatesTags: ['UserAppointments'],
    }),
    validateAppointmentChange: build.mutation<
      IValidateAppointmentChangeResponse,
      IValidateAppointmentChangeRequest
    >({
      query: (body) => ({
        url: `Appointments/ValidateChange`,
        method: "POST",
        body,
      }),
    }),

    getTherapistMonthlyAvailability: build.query<
      ITherapistMonthlyAvailabilityResponse,
      { therapistsGuid: string; month: number; year: number }
    >({
      query: ({ therapistsGuid, month, year }) => ({
        url: `/Appointments/GetTherapistMonthlyAvailability/${therapistsGuid}/${month}/${year}`,
        method: "GET",
      }),
    }),

    getDeviceBookingAvailability: build.query<
      IDeviceBookingAvailabilityResponse,
      { month: number; year: number }
    >({
      query: ({ month, year }) => ({
        url: `/Appointments/GetDeviceBookingAvailability/${month}/${year}`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetUserQuery,
  useLazyGetUserQuery,
  useGetContactDetailsQuery,
  useLazyGetContactDetailsQuery,
  useGetUsersQuery,
  useGetQuestionByQuestionnaireIdQuery,
  useLazyGetQuestionByQuestionnaireIdQuery,
  useLazyGetPOCQuestionByIdQuery,
  useLazyGetCurrentFlowQuery,
  useUpdateUserDetailsMutation,
  useDeleteUserAvatarMutation,
  useUpdateUserSettingsMutation,
  useMakeAppointmentMutation,
  useUpdateAppointmentMutation,
  useCancelAppointmentLocalMutation,
  useFetchDocsQuery,
  useSendFileMutation,
  useFetchPrescriptionsQuery,
  useUpdatePrescriptionMutation,
  useGetCurrentFlowQuery,
  useInsertAnswerQuestionMutation,
  useLazyGetNextQuestionByQuestionnaireIdQuery,
  useGetMessageByUserIdQuery,
  useGetAdminMessagesQuery,
  useGetCurrentQuestionnaireByUsersIdQuery,
  useLazyGetCurrentQuestionnaireByUsersIdQuery,
  useGetMyQuestionnaireTasksQuery,
  useGetMyHotlineQuestionnaireQuery,
  useLazyGetMyQuestionnaireTasksQuery,
  useGetAppointmentsByUserIdQuery,
  useGetSelfBookingPolicyQuery,
  useGetDeviceBookingAvailabilityQuery,
  useLazyGetAppointmentsByUserIdQuery,
  useGetExercisesByUserGuidMutation,
  useInsertAllAnswersQuestionsMutation,
  useGetQuestionnaireByGuidAndTypeQuery,
  useLazyGetQuestionnaireByGuidAndTypeQuery,
  useGetAllRelevantTherapistForUserQuery,
  usePostAppointmentsMutation,
  useCancelAppointmentMutation,
  useGetAllCancelAppointmentsReasonsMutation,
  useAlternativeAppointmentProgramDateMutation,
  useGetAllOpenTreatmentPlansForUserMutation,
  useGetAllAppointmentsInProgramMutation,
  useUploadFileMutation,
  useInsertAudioAnswersQuestionsMutation,
  usePostFilesMutation,
  useValidatePasswordMutation,
  useGetFaqsQuery,
  // New consent hooks
  useGetGeneralConfirmReadStatusQuery,
  useGeneralConfirmReadMutation,
  useGetAnalysisQuery,
  // Protocol hooks
  useLazyGetProtocolComponentsQuery,
  useSwitchPatientMutation,
  useCreateAppointmentFastMutation,
  useValidateAppointmentChangeMutation,
  useGetTherapistMonthlyAvailabilityQuery,
} = userApi;
